import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import * as Sentry from "@sentry/react";
import TopBar from "@/components/topbar"; // Reuse the TopBar for consistency
import { useUser } from "@/zustand/store"; // To access user for greeting, if available

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback = ({ error, resetErrorBoundary }: ErrorFallbackProps) => {
  // Capture the error with Sentry
  Sentry.captureException(error);
  return (
    <div className="bg-bg-primary relative min-h-screen">
      <img
        src="/images/bg-logo.png"
        alt=""
        className="fixed right-1/2 bottom-[calc(50%-100px)] z-0 translate-x-1/2 translate-y-1/2 lg:right-2/5"
      />

      <div className="relative px-4 py-8">
        <div className="mx-auto max-w-4xl">
          <div className="bg-primary-500/90 relative grid w-full grid-cols-1 gap-4 rounded-2xl border border-[#8ECAEC]/30 lg:grid-cols-3 lg:gap-6">
            <div className="col-span-1 lg:col-span-3">
              <div className="thin-scrollbar relative h-full overflow-hidden rounded-2xl bg-[var(--my-subtle-gray-gradient)] p-6 lg:col-span-2">
                <div className="relative z-10 text-white">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-lg font-medium">
                      Oops! An Error Occurred
                    </span>
                    <div
                      className="flex size-8 items-center justify-center rounded-full"
                      style={{
                        background:
                          "linear-gradient(128.62deg, rgba(147,205,253,0.4) 11.02%, rgba(77,134,174,0.4) 93.11%)",
                      }}
                    >
                      <AlertCircle className="text-primary-100 size-4" />
                    </div>
                  </div>
                  <div className="mb-6 flex flex-col items-center text-center">
                    <div className="bg-primary-500/20 mb-4 flex size-16 items-center justify-center rounded-full">
                      <AlertCircle className="text-primary-100 size-8" />
                    </div>
                    <h2 className="mb-2 text-xl font-medium">
                      Something went wrong
                    </h2>
                    <p className="text-primary-100/80 max-w-md text-sm leading-4">
                      We're sorry for the hiccup. Our team has been notified,
                      and we're working to fix it.
                    </p>
                    {/* Dev-only error details */}
                    {import.meta.env.DEV && (
                      <details className="bg-primary-500/10 text-primary-100/70 mt-4 w-full rounded-xl p-3 text-left text-xs">
                        <summary className="mb-2 cursor-pointer font-medium">
                          Error Details (Dev Only)
                        </summary>
                        <pre className="overflow-auto font-mono text-[10px] whitespace-pre-wrap">
                          {error.message}
                        </pre>
                      </details>
                    )}
                  </div>
                  <div className="flex flex-col gap-3 lg:flex-row lg:justify-center">
                    <Button
                      onClick={() => resetErrorBoundary()}
                      variant="ghost"
                      size="sm"
                      className="text-primary-100 hover:bg-primary-100/20 border-primary-300/50 w-full justify-center border lg:w-auto"
                    >
                      Try Again
                    </Button>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="ghost"
                      size="sm"
                      className="text-primary-100 hover:bg-primary-100/20 border-primary-300/50 w-full justify-center border lg:w-auto"
                    >
                      Reload Page
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="bg-primary-500/90 col-span-1 hidden flex-col justify-between rounded-2xl border border-[#8ECAEC]/30 px-4 py-6 text-white backdrop-blur-sm lg:flex">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium">Need Help?</span>
                <div className="bg-primary-100/20 flex size-6 items-center justify-center rounded-full">
                  <AlertCircle className="text-primary-100 size-3" />
                </div>
              </div>
              <p className="text-primary-100/80 mb-4 text-xs leading-4">
                Reach out to support for quick assistance.
              </p>
              <Button
                onClick={() =>
                  window.open("mailto:support@yourapp.com", "_blank")
                }
                variant="ghost"
                size="sm"
                className="text-primary-100 hover:bg-primary-100/20 border-primary-300/50 w-full justify-center border"
              >
                Contact Support
              </Button>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
